import React from "react";
import PropTypes from "prop-types";
import { FiMail, FiLock } from "react-icons/fi";
import VmoContainer from "../Elements/VmoContainer";
import VmoForm from "../Collections/VmoForm/VmoForm";
import VmoFormInput from "../Collections/VmoForm/VmoFormInput";
import VmoButton from "../Elements/VmoButton/VmoButton";
import VmoCard from "../Views/VmoCard/VmoCard";
import VmoCardContent from "../Views/VmoCard/VmoCardContent";
import { Controller, useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { signInSchema } from "../Validation/siginin.schema";
import { useState } from "react";
import VmoFormCheckbox from "../Collections/VmoForm/VmoFormCheckbox";
import VmoBrandLogo from "../Custom/VmoBrandLogo";

export default function VmoSignIn({
  onSignIn = () => null,
  header = "Sign in to Veemo.",
  subHeader = "Enter your Sign-In Credentials",
  btnLabel = "Sign In",
  signUpUrl = "",
  onClickSingUp = () => null,
  onClickForgotPassword = () => null,
  testIds = {},
  btnProps = null,
}) {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    shouldFocusError: true,
    reValidateMode: "onChange",
    submitFocusError: true,
    shouldUnregister: false,
    resolver: joiResolver(signInSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const [showPwd, setShowPwd] = useState(false);
  return (
    <VmoContainer data-testid={testIds.container || "vmo-sign-in-container"}>
      <div className="signInPage">
        <VmoBrandLogo
          className={"veemo-logo"}
          data-testid={testIds.logo || "veemo-logo"}
        />
        <div className="signInBody">
          <VmoCard
            className="loginCard"
            data-testid={testIds.card || "login-card"}
          >
            <VmoCardContent>
              <h1>{header}</h1>
              <p>{subHeader}</p>
              <VmoForm
                className="errorLabel"
                size="large"
                onSubmit={handleSubmit(onSignIn)}
                data-testid={testIds.form || "sign-in-form"}
              >
                <Controller
                  name="email"
                  render={({ field: { value, onChange } }) => (
                    <VmoFormInput
                      autoFocus
                      fluid
                      icon={<FiMail />}
                      iconPosition="left"
                      placeholder="Email Address"
                      error={
                        errors.email && {
                          content: errors.email.message,
                        }
                      }
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                      data-testid={testIds.emailInput || "email-input"}
                    />
                  )}
                  control={control}
                />
                <Controller
                  name="password"
                  render={({ field: { value, onChange } }) => (
                    <VmoFormInput
                      fluid
                      type={!showPwd ? "password" : "text"}
                      icon={<FiLock />}
                      iconPosition="left"
                      placeholder="Password"
                      error={
                        errors?.password && {
                          content: errors?.password?.message,
                        }
                      }
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                      action={{
                        basic: true,
                        icon: !showPwd ? "eye slash" : "eye",
                        onClick: (e) => {
                          e.preventDefault();
                          setShowPwd(!showPwd);
                        },
                      }}
                      data-testid={testIds.passwordInput || "password-input"}
                    />
                  )}
                  control={control}
                />

                <VmoButton
                  primary
                  type="submit"
                  fluid
                  size="large"
                  data-testid={testIds.submitButton || "sign-in-button"}
                  {...btnProps}
                >
                  {btnLabel}
                </VmoButton>
                <div className="remMe">
                  <Controller
                    control={control}
                    name="rememberMe"
                    render={({ field: { value, onChange } }) => (
                      <VmoFormCheckbox
                        label="Remember me"
                        checked={value}
                        onClick={(e) => onChange(!value)}
                        data-testid={
                          testIds.rememberMeCheckbox || "remember-me-checkbox"
                        }
                      />
                    )}
                  />
                  <span
                    onClick={onClickForgotPassword}
                    data-testid={
                      testIds.forgotPasswordLink || "forgot-password-link"
                    }
                  >
                    Forgot Password?
                  </span>
                </div>
              </VmoForm>
            </VmoCardContent>
          </VmoCard>
        </div>
        <p className="vmo-lead mt-4 cursor-pointer">
          Don't have an account?{" "}
          <a
            href={signUpUrl}
            className="blue-link"
            onClick={onClickSingUp}
            data-testid={testIds.signUpLink || "sign-up-link"}
            target="_self"
          >
            Sign up
          </a>
        </p>
      </div>
    </VmoContainer>
  );
}

VmoSignIn.propTypes = {
  onSignIn: PropTypes.func.isRequired,
  header: PropTypes.string,
  subHeader: PropTypes.string,
  btnLabel: PropTypes.string,
  signUpUrl: PropTypes.string.isRequired,
  onClickSingUp: PropTypes.func,
  onClickForgotPassword: PropTypes.func.isRequired,
  testIds: PropTypes.shape({
    container: PropTypes.string,
    logo: PropTypes.string,
    card: PropTypes.string,
    form: PropTypes.string,
    emailInput: PropTypes.string,
    passwordInput: PropTypes.string,
    submitButton: PropTypes.string,
    rememberMeCheckbox: PropTypes.string,
    forgotPasswordLink: PropTypes.string,
    signUpLink: PropTypes.string,
  }),
};

// Define defaultProps for your component
VmoSignIn.defaultProps = {
  onSignIn: () => null,
  header: "Sign in to Veemo.",
  subHeader: "Enter your Sign-In Credentials",
  btnLabel: "Sign In",
  signUpUrl: "/",
  onClickSingUp: () => null,
  onClickForgotPassword: () => null,
  testIds: {},
};
